import { DEFAULT_CONTENT } from './defaults';
import { AssessmentContent, AssessmentContentInput } from './types';

/** Use the CMS string only when it's a non-empty value; otherwise the default. */
const str = (value: unknown, fallback: string): string => {
  if (typeof value === 'string' && value.trim() !== '') return value;
  return fallback;
};

/**
 * Resolve a free-length list (rendered with .map, e.g. bullet lists): take the
 * CMS list when it has at least one non-empty entry, otherwise the default.
 */
const list = (value: unknown, fallback: string[]): string[] => {
  if (Array.isArray(value)) {
    const cleaned = value.filter((v): v is string => typeof v === 'string' && v.trim() !== '');
    if (cleaned.length > 0) return cleaned;
  }
  return fallback;
};

/**
 * Resolve a fixed-length list that is matched to code logic by position
 * (questions, tiers, insights, pillars). Always returns exactly
 * `fallback.length` items, filling each slot from the CMS when present.
 */
const byIndex = <T>(value: unknown, fallback: T[], pick: (cmsItem: unknown, fallbackItem: T) => T): T[] => {
  const cms = Array.isArray(value) ? value : [];
  return fallback.map((fb, i) => pick(cms[i], fb));
};

/**
 * Merge a Sanity `aiNativeAssessment` document over the built-in defaults.
 * Any missing or empty field falls back to the default, so the page renders
 * identically to its pre-CMS state when the document is empty or absent.
 */
export function resolveAssessmentContent(input: AssessmentContentInput): AssessmentContent {
  const d = DEFAULT_CONTENT;
  const c = input ?? {};

  const sidePanel = c.sidePanel ?? {};
  const intro = c.intro ?? {};
  const gate = c.gate ?? {};
  const results = c.results ?? {};
  const confirm = c.confirm ?? {};

  return {
    metaTitle: str(c.metaTitle, d.metaTitle),
    metaDescription: str(c.metaDescription, d.metaDescription),

    navPrimary: str(c.navPrimary, d.navPrimary),
    navSecondary: str(c.navSecondary, d.navSecondary),
    footerCopyright: str(c.footerCopyright, d.footerCopyright),

    sidePanel: {
      headlineLead: str(sidePanel.headlineLead, d.sidePanel.headlineLead),
      headlineHighlight: str(sidePanel.headlineHighlight, d.sidePanel.headlineHighlight),
      paragraphs: byIndex(sidePanel.paragraphs, d.sidePanel.paragraphs, (cms, fb) => str(cms, fb)),
      youllGetTitle: str(sidePanel.youllGetTitle, d.sidePanel.youllGetTitle),
      youllGet: list(sidePanel.youllGet, d.sidePanel.youllGet),
      timeBadge: str(sidePanel.timeBadge, d.sidePanel.timeBadge),
    },

    intro: {
      eyebrow: str(intro.eyebrow, d.intro.eyebrow),
      title: str(intro.title, d.intro.title),
      subtitle: str(intro.subtitle, d.intro.subtitle),
      statQuestionsLabel: str(intro.statQuestionsLabel, d.intro.statQuestionsLabel),
      statDimensionsLabel: str(intro.statDimensionsLabel, d.intro.statDimensionsLabel),
      statTimeValue: str(intro.statTimeValue, d.intro.statTimeValue),
      statTimeLabel: str(intro.statTimeLabel, d.intro.statTimeLabel),
      ctaLabel: str(intro.ctaLabel, d.intro.ctaLabel),
    },

    pillars: byIndex(c.pillars, d.pillars, (cms, fb) => str(cms, fb)),

    questions: byIndex(c.questions, d.questions, (cms, fb) => {
      const item = (cms ?? {}) as { question?: unknown; options?: unknown };
      return {
        question: str(item.question, fb.question),
        options: byIndex(item.options, fb.options, (o, ofb) => str(o, ofb)),
      };
    }),

    tiers: byIndex(c.tiers, d.tiers, (cms, fb) => {
      const item = (cms ?? {}) as { label?: unknown; range?: unknown };
      return {
        label: str(item.label, fb.label),
        range: str(item.range, fb.range),
      };
    }),

    tierSummaries: byIndex(c.tierSummaries, d.tierSummaries, (cms, fb) => str(cms, fb)),

    insights: byIndex(c.insights, d.insights, (cms, fb) => {
      const item = (cms ?? {}) as { strong?: unknown; weak?: unknown };
      return {
        strong: str(item.strong, fb.strong),
        weak: str(item.weak, fb.weak),
      };
    }),

    gate: {
      categoryLabel: str(gate.categoryLabel, d.gate.categoryLabel),
      title: str(gate.title, d.gate.title),
      subtitle: str(gate.subtitle, d.gate.subtitle),
      firstNameLabel: str(gate.firstNameLabel, d.gate.firstNameLabel),
      firstNamePlaceholder: str(gate.firstNamePlaceholder, d.gate.firstNamePlaceholder),
      lastNameLabel: str(gate.lastNameLabel, d.gate.lastNameLabel),
      lastNamePlaceholder: str(gate.lastNamePlaceholder, d.gate.lastNamePlaceholder),
      emailLabel: str(gate.emailLabel, d.gate.emailLabel),
      emailPlaceholder: str(gate.emailPlaceholder, d.gate.emailPlaceholder),
      companyLabel: str(gate.companyLabel, d.gate.companyLabel),
      companyPlaceholder: str(gate.companyPlaceholder, d.gate.companyPlaceholder),
      companySizeLabel: str(gate.companySizeLabel, d.gate.companySizeLabel),
      roleLabel: str(gate.roleLabel, d.gate.roleLabel),
      roleOptions: list(gate.roleOptions, d.gate.roleOptions),
      privacyNote: str(gate.privacyNote, d.gate.privacyNote),
      submitLabel: str(gate.submitLabel, d.gate.submitLabel),
    },

    results: {
      navLabel: str(results.navLabel, d.results.navLabel),
      eyebrow: str(results.eyebrow, d.results.eyebrow),
      title: str(results.title, d.results.title),
      outOfLabel: str(results.outOfLabel, d.results.outOfLabel),
      ctaPrimary: str(results.ctaPrimary, d.results.ctaPrimary),
      ctaSecondary: str(results.ctaSecondary, d.results.ctaSecondary),
      spiderTitle: str(results.spiderTitle, d.results.spiderTitle),
      legendYour: str(results.legendYour, d.results.legendYour),
      legendPeer: str(results.legendPeer, d.results.legendPeer),
      pillarBreakdownTitle: str(results.pillarBreakdownTitle, d.results.pillarBreakdownTitle),
      tierTitle: str(results.tierTitle, d.results.tierTitle),
      insightsTitle: str(results.insightsTitle, d.results.insightsTitle),
    },

    confirm: {
      navSecondary: str(confirm.navSecondary, d.confirm.navSecondary),
      title: str(confirm.title, d.confirm.title),
      body: str(confirm.body, d.confirm.body),
      tierCaption: str(confirm.tierCaption, d.confirm.tierCaption),
      meetingEyebrow: str(confirm.meetingEyebrow, d.confirm.meetingEyebrow),
      meetingTitle: str(confirm.meetingTitle, d.confirm.meetingTitle),
      meetingMeta: str(confirm.meetingMeta, d.confirm.meetingMeta),
      skipLabel: str(confirm.skipLabel, d.confirm.skipLabel),
    },
  };
}
